import { Box, Typography } from '@mui/material';
import { deleteObjectApi, putUpdateObjectApi } from 'api/general';
import { postMoveDecisionFilesApi } from 'api/meeting';
import { flattenKurulDisplayOrder, subjectGroupsFromAgendas, updateSortedAgendaList } from 'app/utils/appHelpers';
import { DECISION } from 'app/utils/constants/customConstant';
import { departmentCardColor, subjectCardTextColor } from 'config';
import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { setCurrentAgenda, setCurrentAgendaList, showAgendaSnackbar } from 'stores/meeting';
import Swal from 'sweetalert2';
import AddPendingAgendaDialog from '../Dialog/AddPendingAgendaDialog';
import AddUpdateAgendaDialog from '../Dialog/AddUpdateAgendaDialog';
import PendingAgendaConfirmDialog from '../Dialog/PendingAgendaConfirmDialog';
import AgendaSubjectCard from './AgendaSubjectCard';
import LoadingPopUp from '../LoadingPopup';
import { AgendaProcessingContext, AgendaReviewVoteContext } from './AgendaCard';
import { agendaMatchesSearch, useAgendaListSearch, useRegisterAgendaListActions } from './AgendaListActions';
import { updateMeetingAfterUserOperation } from 'app/utils/meetingReduxHelpers';

function AgendaCardListKurul() {
    const [data, setData] = useState([]);
    const dispatch = useDispatch();
    const [addAgendaDialogStatus, setAddAgendaDialogStatus] = useState(false);
    const [pendingAgendaDialogStatus, setPendingAgendaDialogStatus] = useState(false);
    const [pendingConfirmItem, setPendingConfirmItem] = useState(null);
    const currentMeeting = useSelector((state) => state.meeting.currentMeeting);
    const { authUser } = useSelector((state) => state.auth);
    const [subjectGroups, setSubjectGroups] = useState([]);
    const currentAgendaList = useSelector((state) => state.meeting.currentAgendaList);
    const currentMeetingReviewList = useSelector((state) => state.meeting.currentMeetingReviewList);
    const currentMeetingVoteList = useSelector((state) => state.meeting.currentMeetingVoteList);
    const [extraGroup, setExtraGroup] = useState([]);
    const [busy, setBusy] = useState(false);
    const [sorting, setSorting] = useState(false);

    const withBusy = async (fn) => {
        setBusy(true);
        try {
            await fn();
        } finally {
            setBusy(false);
        }
    };

    const withSorting = async (fn) => {
        if (sorting) return;
        setSorting(true);
        try {
            await fn();
        } finally {
            setSorting(false);
        }
    };

    useEffect(() => {
        const allGroups = subjectGroupsFromAgendas(currentAgendaList);
        const extras = currentAgendaList.filter(a => a.supp === 'Evet');
        const normalGroups = allGroups
            .map(([subj, ags]) => [subj, ags.filter(a => a.supp !== 'Evet')])
            .filter(([, ags]) => ags.length > 0);
        setSubjectGroups(normalGroups);
        setExtraGroup(extras);

        setData(currentAgendaList);
    }, [currentAgendaList]);


    const handleExtraAgendaSorting = async (subjectId, agendaIndex, direction, updatedAgenda) => {
        await withBusy(async () => {

        // Subject bazında grubu bul ve kendi içinde sırala
        const sg = subjectGroupsFromAgendas(extraGroup);
        const gi = sg.findIndex(([sid]) => sid == subjectId);
        if (gi === -1) return;

        let group = [...sg[gi][1]];
        if (direction === 'none') {
            const targetIdx = updatedAgenda.decisionNo - 1;
            group = group.filter(a => a.id !== updatedAgenda.id);
            group.splice(targetIdx, 0, updatedAgenda);
        } else if (direction === 'up' && agendaIndex > 0) {
            [group[agendaIndex - 1], group[agendaIndex]] = [group[agendaIndex], group[agendaIndex - 1]];
        } else if (direction === 'down' && agendaIndex < group.length - 1) {
            [group[agendaIndex], group[agendaIndex + 1]] = [group[agendaIndex + 1], group[agendaIndex]];
        }

        sg[gi][1] = group;
        const newExtra = sg.flatMap(([, ags]) => ags);
        setExtraGroup(newExtra);

        const all = [
            ...subjectGroups.flatMap(([, ags]) => ags),
            ...newExtra
        ];

        dispatch(await updateSortedAgendaList(all, "index", [], true));
        });
    };

    const handleSubjectSorting = async (subjectIndex, direction) => {
        await withBusy(async () => {
        const newSubjectGroups = [...subjectGroups];
        if (direction === 'up' && subjectIndex > 0) {
            [newSubjectGroups[subjectIndex - 1], newSubjectGroups[subjectIndex]] =
                [newSubjectGroups[subjectIndex], newSubjectGroups[subjectIndex - 1]];
        } else if (direction === 'down' && subjectIndex < newSubjectGroups.length - 1) {
            [newSubjectGroups[subjectIndex], newSubjectGroups[subjectIndex + 1]] =
                [newSubjectGroups[subjectIndex + 1], newSubjectGroups[subjectIndex]];
        }
        setSubjectGroups(newSubjectGroups);
        const newFlatArray = newSubjectGroups.flatMap(([subjectId, agendas]) => agendas);
        dispatch(await updateSortedAgendaList(newFlatArray, "index", [], true));
        });
    };

    const handleAgendaSorting = async (subjectId, agendaIndex, direction, updatedAgenda) => {
        await withBusy(async () => {
        const groupIndex = subjectGroups.findIndex(([subjId]) => subjId == subjectId);
        if (groupIndex === -1) return;
        let agendas = subjectGroups[groupIndex][1];
        let newAgendas = [...agendas];
        if (direction === 'none') {
            const targetIndex = updatedAgenda.decisionNo - 1;
            newAgendas = newAgendas.filter(a => a.id !== updatedAgenda.id);
            newAgendas.splice(targetIndex, 0, updatedAgenda);
        } else {
            if (direction === 'up' && agendaIndex > 0) {
                [newAgendas[agendaIndex - 1], newAgendas[agendaIndex]] =
                    [newAgendas[agendaIndex], newAgendas[agendaIndex - 1]];
            } else if (direction === 'down' && agendaIndex < newAgendas.length - 1) {
                [newAgendas[agendaIndex], newAgendas[agendaIndex + 1]] =
                    [newAgendas[agendaIndex + 1], newAgendas[agendaIndex]];
            }
        }
        const newSubjectGroups = subjectGroups.map(([subjId, ags], idx) => {
            if (idx === groupIndex) return [subjId, newAgendas];
            return [subjId, ags];
        });
        setSubjectGroups(newSubjectGroups);


        const newFlatArray = [
            ...newSubjectGroups.flatMap(([_, ags]) => ags),
            ...extraGroup
        ];

        dispatch(await updateSortedAgendaList(newFlatArray, "index", [], true));
        });
    };


    const handleDelete = async (item, type = "agenda", pendingText) => {

        if (type === 'pending' && pendingText === undefined) {
            setPendingConfirmItem(item);
            return;
        }

        if (type !== 'pending') {
            const confirmResult = await Swal.fire({
                title: 'Silme İşlemi',
                text: 'Silme işlemini onaylıyor musunuz?',
                icon: 'warning',
                showCancelButton: true,
                confirmButtonText: 'Evet',
                cancelButtonText: 'Vazgeç',
                customClass: { container: 'swal-container-custom' }
            });
            if (!confirmResult.isConfirmed) {
                return;
            }
        }

        let operationOk = false;
        await withBusy(async () => {
        let updatedList = [];
        if (type === "agenda") {

            const result = await deleteObjectApi(DECISION, item.id);
            if (result.status) {

                const sortedAgendaList = [...data].sort((a, b) => a.decisionNo - b.decisionNo);
                const filteredList = sortedAgendaList.filter(x => x.id !== item.id);


                if (filteredList.length > 0) {
                    updatedList = filteredList.map((agenda, index) => ({
                        ...agenda,
                        decisionNo: index + 1,
                    }));
                    await dispatch(await updateSortedAgendaList(updatedList, "", [], true));

                } else {

                    if (authUser.id !== currentMeeting.adminUserId) {
                        await updateMeetingAfterUserOperation(
                            currentMeeting,
                            authUser,
                            dispatch
                        );
                    }

                    dispatch(setCurrentAgendaList([]));
                }
                operationOk = true;

            } else {
                dispatch(showAgendaSnackbar({
                    message: 'Silme işlemi başarısız.',
                    severity: 'error'
                }));
                return;
            }


        } else if (type === "pending") {

            let { user, ...pendingAgenda } = item;

            pendingAgenda = {
                ...pendingAgenda,
                meetingId: -1,
                pendingUnitId: currentMeeting.unitId,
                pendingText: pendingText
            }
            const result = await putUpdateObjectApi(DECISION, pendingAgenda);


            if (result.status) {

                const sortedAgendaList_ = [...data].sort((a, b) => a.decisionNo - b.decisionNo);
                const filteredList = sortedAgendaList_.filter(x => x.id !== item.id);


                if (filteredList.length > 0) {
                    updatedList = filteredList.map((agenda, index) => ({
                        ...agenda,
                        decisionNo: index + 1,
                    }));

                    await dispatch(await updateSortedAgendaList(updatedList, "", [], true));

                } else {

                    if (authUser.id !== currentMeeting.adminUserId) {
                        await updateMeetingAfterUserOperation(
                            currentMeeting,
                            authUser,
                            dispatch
                        );
                    }
                    dispatch(setCurrentAgendaList([]));


                }
                operationOk = true;

            } else {
                dispatch(showAgendaSnackbar({
                    message: 'Askıya alma işlemi başarısız.',
                    severity: 'error'
                }));
                return;
            }

        }
        });

        if (!operationOk) {
            return;
        }

        if (type === "agenda") {
            dispatch(showAgendaSnackbar({
                message: `${item.decisionNo} numaralı gündem silindi.`,
                severity: 'error'
            }));
        } else if (type === "pending") {
            dispatch(showAgendaSnackbar({
                message: `${item.decisionNo} numaralı gündem askıya alındı.`,
                severity: 'warning'
            }));
        }

    };



    const closeDialog = () => {
        setAddAgendaDialogStatus(false);
    };

    const updateAgenda = () => {
        setAddAgendaDialogStatus(true);
        dispatch(setCurrentAgenda(null));
    };


    const sortButtonClick = async () => {
        await withSorting(async () => {
        const normals = currentAgendaList.filter((a) => a.supp !== 'Evet');
        const extras = currentAgendaList.filter((a) => a.supp === 'Evet');
        const allAgendas = [
            ...flattenKurulDisplayOrder(normals),
            ...flattenKurulDisplayOrder(extras)
        ];

        const savedList = await dispatch(await updateSortedAgendaList(allAgendas, "index", [], true));
        if (savedList) {
            dispatch(showAgendaSnackbar({
                message: 'Sıralama başarıyla güncellendi.',
                severity: 'success'
            }));
        } else {
            dispatch(showAgendaSnackbar({
                message: 'Sıralama kaydedilemedi.',
                severity: 'error'
            }));
        }
        });
    };

    const addPendingAgenda = () => {
        setPendingAgendaDialogStatus(true);
        dispatch(setCurrentAgenda(null));
    };

    const handleAddPendingAgenda = async (agendaItem) => {
        let newAgenda = { ...agendaItem };
        if (!newAgenda.decision || (typeof newAgenda.decision === "string" && newAgenda.decision.trim() === "")) {
            newAgenda.decision = newAgenda.agenda;
        }
        newAgenda.meetingId = currentMeeting.id;
        newAgenda.adminUserId = authUser.id;
        newAgenda.agenda = agendaItem.agenda;
        newAgenda.decision = agendaItem.decision;
        newAgenda.pdate = new Date();
        newAgenda.decisionNo = 0;
        const result = await putUpdateObjectApi(DECISION, newAgenda);
        if (result.status) {
            try {
                const moveResult = await postMoveDecisionFilesApi({
                    decisionId: agendaItem.id,
                    newMeetingId: currentMeeting.id,
                    newInst: currentMeeting.inst
                });
                if (!moveResult.status) {
                    const { user, ...revertAgenda } = agendaItem;
                    await putUpdateObjectApi(DECISION, revertAgenda);
                    Swal.fire({
                        position: 'center',
                        icon: 'error',
                        title: 'Dosya taşıma sırasında hata oluştu. İşlem geri alındı.',
                        showConfirmButton: true,
                        customClass: { container: 'swal-container-custom' },
                    });
                    return;
                }
            } catch (err) {
                const { user, ...revertAgenda } = agendaItem;
                await putUpdateObjectApi(DECISION, revertAgenda);
                Swal.fire({
                    position: 'center',
                    icon: 'error',
                    title: 'Dosya taşıma sırasında hata oluştu. İşlem geri alındı.',
                    showConfirmButton: true,
                    customClass: { container: 'swal-container-custom' },
                });
                return;
            }

            const newAgendaWithUser = { id: result.data.insertId, ...newAgenda, user: authUser }
            const savedList = await dispatch(await updateSortedAgendaList(currentAgendaList, "change", newAgendaWithUser, true));
            const saved = Array.isArray(savedList)
                ? savedList.find((item) => item.id === newAgendaWithUser.id)
                : null;
            dispatch(showAgendaSnackbar({
                message: `${saved?.decisionNo ?? newAgenda.decisionNo} numaralı gündem eklendi.`,
                severity: 'success'
            }));

            setPendingAgendaDialogStatus(false);
        }
    };

    const reviewsByDecisionId = {};
    const votesByDecisionId = {};
    currentAgendaList.forEach((a) => {
        reviewsByDecisionId[a.id] = [];
        votesByDecisionId[a.id] = [];
    });
    if (currentMeetingReviewList) {
        currentMeetingReviewList.forEach((item) => {
            if (reviewsByDecisionId[item.decisionId]) {
                reviewsByDecisionId[item.decisionId].push(item);
            }
        });
    }
    if (currentMeetingVoteList) {
        currentMeetingVoteList.forEach((item) => {
            if (votesByDecisionId[item.decisionId]) {
                votesByDecisionId[item.decisionId].push(item);
            }
        });
    }

    useRegisterAgendaListActions({
        onSort: sortButtonClick,
        sortTooltip: 'Gündem numaraları ardışık sıralanmadığında kullanınız',
        onAddAgenda: updateAgenda,
        onAddPending: addPendingAgenda,
        sorting,
    });

    const { searchQuery } = useAgendaListSearch();
    const hasSearchQuery = Boolean(searchQuery.trim());
    const visibleExtraGroup = useMemo(
        () => extraGroup.filter((agenda) => agendaMatchesSearch(agenda, searchQuery)),
        [extraGroup, searchQuery]
    );
    const visibleSubjectCount = useMemo(
        () => subjectGroups.reduce((count, [, agendas]) => (
            count + agendas.filter((agenda) => agendaMatchesSearch(agenda, searchQuery)).length
        ), 0),
        [subjectGroups, searchQuery]
    );
    const hasVisibleAgendas = visibleSubjectCount > 0 || visibleExtraGroup.length > 0;

    return (
        <AgendaProcessingContext.Provider value={{ setProcessing: setBusy }}>
        <AgendaReviewVoteContext.Provider value={{ reviewsByDecisionId, votesByDecisionId }}>
            <>
                <LoadingPopUp open={busy} blocking message="" />

                {addAgendaDialogStatus && (
                    <AddUpdateAgendaDialog
                        open={addAgendaDialogStatus}
                        onClose={closeDialog}
                    />
                )}

                {pendingAgendaDialogStatus && (
                    <AddPendingAgendaDialog
                        handleAddPendingAgenda={handleAddPendingAgenda}
                        open={pendingAgendaDialogStatus}
                        onClose={() => setPendingAgendaDialogStatus(false)}
                    />
                )}

                <PendingAgendaConfirmDialog
                    open={Boolean(pendingConfirmItem)}
                    agendaId={pendingConfirmItem?.id}
                    onClose={() => setPendingConfirmItem(null)}
                    onConfirm={(pendingText) => {
                        handleDelete(pendingConfirmItem, 'pending', pendingText);
                        setPendingConfirmItem(null);
                    }}
                />

                {currentAgendaList.length > 0 && hasSearchQuery && !hasVisibleAgendas &&
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            height: '50%',
                            color: 'black'
                        }}
                    >
                        <Typography variant="h6" color="inherit">
                            Eşleşen gündem bulunamadı
                        </Typography>
                    </Box>
                }

                {subjectGroups.map(([subjectId, agendas], index) => {
                    const visibleAgendas = hasSearchQuery
                        ? agendas.filter((agenda) => agendaMatchesSearch(agenda, searchQuery))
                        : agendas;
                    if (visibleAgendas.length === 0) {
                        return null;
                    }
                    return (
                    <AgendaSubjectCard
                        key={subjectId}
                        subjectId={subjectId}
                        agendas={visibleAgendas}
                        index={index}
                        subjectGroups={subjectGroups}
                        handleSubjectSorting={handleSubjectSorting}
                        handleAgendaSorting={handleAgendaSorting}
                        handleDelete={handleDelete}
                    />
                    );
                })}


                {visibleExtraGroup.length > 0 && (
                    <>
                        <Box sx={{ mt: 1, mb: 1 }}>
                            <Typography sx={{ color: 'error.main', fontWeight: 500, fontSize: '1rem', textAlign: 'center' }}>
                                Ek Gündemler / Gündem Sayısı: {visibleExtraGroup.length}
                            </Typography>
                        </Box>

                        {subjectGroupsFromAgendas(visibleExtraGroup).map(([subjectId, agendas], idx, arr) => (
                            <AgendaSubjectCard
                                key={`extra-${subjectId}`}
                                subjectId={subjectId}
                                agendas={agendas}
                                index={idx}
                                subjectGroups={arr}
                                handleSubjectSorting={async (subjectIndex, direction) => {
                                    await withBusy(async () => {

                                    const groups = subjectGroupsFromAgendas(extraGroup);
                                    let newGroups = [...groups];
                                    if (direction === 'up' && subjectIndex > 0) {
                                        [newGroups[subjectIndex - 1], newGroups[subjectIndex]] = [newGroups[subjectIndex], newGroups[subjectIndex - 1]];
                                    } else if (direction === 'down' && subjectIndex < newGroups.length - 1) {
                                        [newGroups[subjectIndex], newGroups[subjectIndex + 1]] = [newGroups[subjectIndex + 1], newGroups[subjectIndex]];
                                    }
                                    const newExtra = newGroups.flatMap(([, ags]) => ags);
                                    setExtraGroup(newExtra);
                                    const all = [
                                        ...subjectGroups.flatMap(([, ags]) => ags),
                                        ...newExtra
                                    ];
                                    await dispatch(await updateSortedAgendaList(all, 'index', [], true));
                                    });
                                }}
                                handleAgendaSorting={(subjectId, agendaIndex, direction, updatedAgenda) =>
                                    handleExtraAgendaSorting(subjectId, agendaIndex, direction, updatedAgenda)
                                }
                                handleDelete={handleDelete}
                            />
                        ))}
                    </>
                )}

            </>
        </AgendaReviewVoteContext.Provider>
        </AgendaProcessingContext.Provider>
    );
}

const style = document.createElement('style');
style.innerHTML = `
    .swal-container-custom {
        z-index: 10000 !important;
    }
`;
document.head.appendChild(style);


export default AgendaCardListKurul;
